import { StockSupplier, StockReception, StockReceptionItem, StockReceiptPayload } from 'types/models/StockReceipt';
import axiosInstance, { axiosMultipart } from './axios';
import PaginationData from 'types/models/PaginationData';
import Product from 'types/models/Product';
import joinURLParams from 'utils/joinUrlParams';
import { parseProductionCenter } from './stockProductionCenterServices';
import { parseWarehouse } from './stockWarehouseServices';

const RECEPTIONS_URL = 'stock/receptions';
export const getStockReceptions = async (
  page?: number,
  searchTerm?: string,
  dateFrom?: string,
  dateTo?: string,
  status?: string,
  venueId?: string,
): Promise<PaginationData<StockReception>> => {
  const url = joinURLParams({ page, searchTerm, dateFrom, dateTo, status, venueId }, RECEPTIONS_URL);
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url,
    });
    return {
      currentPage: data.current_page,
      data: parseReceptions(data.data),
      perPage: data.per_page,
      total: data.total,
    };
  } catch (error) {
    throw error;
  }
};

export const getStockReception = async (id: string): Promise<StockReception> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `${RECEPTIONS_URL}/${id}`,
    });
    return parseReception(data);
  } catch (error) {
    throw error;
  }
};

export const storeStockReception = async (payload: StockReceiptPayload | FormData): Promise<StockReception> => {
  try {
    const isFormData = payload instanceof FormData;
    const { data } = await (isFormData ? axiosMultipart : axiosInstance)({
      method: 'post',
      url: RECEPTIONS_URL,
      data: payload,
    });
    return parseReception(data);
  } catch (error) {
    throw error;
  }
};

export const updateStockReception = async (
  id: string,
  payload: StockReceiptPayload | FormData,
): Promise<StockReception> => {
  try {
    const isFormData = payload instanceof FormData;
    const { data } = await (isFormData ? axiosMultipart : axiosInstance)({
      method: isFormData ? 'post' : 'put',
      url: isFormData ? `${RECEPTIONS_URL}/${id}?_method=put` : `${RECEPTIONS_URL}/${id}`,
      data: payload,
    });
    return parseReception(data);
  } catch (error) {
    throw error;
  }
};

export const confirmStockReception = async (id: string): Promise<StockReception> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `${RECEPTIONS_URL}/${id}/confirm`,
    });
    return parseReception(data);
  } catch (error) {
    throw error;
  }
};

export const getStockProviders = async (): Promise<PaginationData<StockSupplier>> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url: 'stock/providers',
    });

    return {
      currentPage: paginationData.current_page,
      data: parseProviders(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    throw error;
  }
};

export const cancelStockReception = async (id: string, cancelledByUserId?: string): Promise<StockReception> => {
  const { data } = await axiosInstance({
    method: 'POST',
    url: `${RECEPTIONS_URL}/${id}/cancel`,
    data: { cancelledByUserId },
  });
  return parseReception(data);
};

const parseProviders = (data: unknown[]): StockSupplier[] => {
  return data.map(parseStockSupplier);
};

const parseStockSupplier = (item: any): StockSupplier => {
  return {
    id: item.id,
    businessName: item.business_name,
    taxId: item.tax_id,
    address: item.address,
    phone: item.phone,
    email: item.email,
    isActive: item.is_active,
  };
};

const parseReceptions = (data: unknown[]): StockReception[] => {
  return data.map(parseReception);
};

const parseReception = (item: any): StockReception => {
  return {
    id: item.id,
    venueId: item.venue_id,
    receiptFileUrl: item.receipt_file_url ?? null,
    purchaseOrderFileUrl: item.purchase_order_file_url ?? null,
    observations: item.observations,
    receivedAt: item.received_at,
    totalAmount: item.total_amount,
    status: item.status,

    originType: item.origin_type,
    originSupplierId: item.origin_supplier_id,
    originProductionCenterId: item.origin_production_center_id,
    destinationWarehouseId: item.destination_warehouse_id,
    originSupplier: item.origin_supplier ? parseStockSupplier(item.origin_supplier) : undefined,
    originProductionCenter: item.origin_production_center
      ? parseProductionCenter(item.origin_production_center)
      : undefined,

    destinationWarehouse: item.destination_warehouse ? parseWarehouse(item.destination_warehouse) : undefined,
    items: Array.isArray(item.items) ? parseReceptionItems(item.items) : [],
  };
};

const parseReceptionItems = (items: unknown[]): StockReceptionItem[] => {
  return items.map(parseReceptionItem);
};

const parseReceptionItem = (item: any): StockReceptionItem => {
  return {
    id: item.id,
    name: item.name,
    sku: item.sku,
    quantity: item.quantity,
    type: item.type,
    unitCost: item.unit_cost,
    globalProductId: item.global_product_id,
  };
};

export const getVenueProducts = async (venueId: string): Promise<Product[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `/products/get/by/venue/${venueId}`,
    });
    return data;
  } catch (error) {
    throw error;
  }
};

export const getVenueProviders = async (venueId: string): Promise<StockSupplier[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `stock/providers/get/by/venue/${venueId}`,
    });
    return parseProviders(data);
  } catch (error) {
    throw error;
  }
};
